import React, { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { CometChatCalls } from "@cometchat/calls-sdk-javascript";

interface VideoCallScreenProps {
  sessionId: string;
  patientName: string;
  doctorName: string;
  onEndCall: () => void;
}

export function VideoCallScreen({
  sessionId,
  patientName,
  doctorName,
  onEndCall,
}: VideoCallScreenProps) {
  const [callToken, setCallToken] = useState("");
  const [callDuration, setCallDuration] = useState(0);

  const callContainerRef = useRef<HTMLDivElement | null>(null);

  // Timer
  useEffect(() => {
    const intv = setInterval(() => setCallDuration((t) => t + 1), 1000);
    return () => clearInterval(intv);
  }, []);

  const formatTime = (sec: number) =>
    `${String(Math.floor(sec / 60)).padStart(2, "0")}:${String(
      sec % 60
    ).padStart(2, "0")}`;

  // Fetch token
  useEffect(() => {
    const fetchToken = async () => {
      try {
        const loggedInUser = await CometChat.getLoggedinUser();
        if (!loggedInUser) return;

        const authToken = loggedInUser.getAuthToken();
        const res = await CometChatCalls.generateToken(sessionId, authToken);

        setCallToken(res.token);
      } catch (err) {
        console.error("Token Error:", err);
      }
    };

    fetchToken();
  }, [sessionId]);

  // Start Call Session
  useEffect(() => {
    if (!callToken || !callContainerRef.current) return;

    const callSettings = new CometChatCalls.CallSettingsBuilder()
      .enableDefaultLayout(true)           // IMPORTANT → uses CometChat UI
      .setIsAudioOnlyCall(false)
      .startWithVideoMuted(false)
      .setCallListener(
        new CometChatCalls.OngoingCallListener({
          onCallEnded: () => {
            endCall();
          },
          onCallEndButtonPressed: () => {
            endCall();
          },
          onError: (e) => console.log("Call error:", e),
        })
      )
      .build();

    CometChatCalls.startSession(
      callToken,
      callSettings,
      callContainerRef.current
    );
  }, [callToken]);

  // Handle End Call
  const endCall = async () => {
    try {
      await CometChatCalls.endSession();
    } catch (e) {
      console.warn("End Session error:", e);
    }

    // Cleanup tracks
    document.querySelectorAll("video").forEach((vid: HTMLVideoElement) => {
      vid.srcObject &&
        (vid.srcObject as MediaStream).getTracks().forEach((t) => t.stop());
      vid.srcObject = null;
    });

    onEndCall();
  };

  return (
    <div className="fixed inset-0 bg-black text-white flex flex-col z-50">

      {/* HEADER */}
      <div className="p-4 px-6 flex justify-between items-center bg-[#1e1e1e] border-b border-white/20">
        <div className="font-medium text-lg">
          {patientName} • {doctorName}
        </div>

        <Badge className="bg-red-600/20 text-red-400 border-red-600">
          <span className="w-2 h-2 bg-red-600 rounded-full mr-2 animate-pulse" />
          {formatTime(callDuration)}
        </Badge>

        <Button onClick={endCall} variant="ghost" className="text-white">
          <X />
        </Button>
      </div>

      {/* VIDEO AREA (only CometChat UI) */}
      <div className="flex-1 relative">
        <div
          ref={callContainerRef}
          id="cometchat-call-container"
          style={{
            width: "100%",
            height: "100%",
            position: "absolute",
            inset: 0,
            backgroundColor: "black",
          }}
        />
      </div>
    </div>
  );
}
